import classnames from "classnames";
import _, {map} from 'lodash';
import React, {useRef, useState} from 'react';
import Confetti from "react-confetti";
import { Img } from "react-image";
import { breakpoints } from "../../styles";
import {getAppMedia, mediaTypes} from '../../utils/media';
import moment from "../../utils/moment-timezone-with-data";
import AllergensDecorator from "../allergens-decorator";
import AppContainer from "../AppContainer";
import CalendarIcon from "../icons/Calendar.svg";
import GiftIcon from "../icons/Gift.svg";
import LockClosedIcon from "../icons/LockClosed.svg";
import LockOpenIcon from "../icons/LockOpen.svg";
import PlateIcon from "../icons/Plate.svg";
import Image from "../image";
import List from "../List";
import Loader from "../Loader";
import * as styles from "./index.module.scss";
import FavoriteButton from '../FavoriteButton/FavoriteButton';
import {Box} from '@mui/system';
import Slider from 'react-slick';
import {makeStyles} from '../AppContainer/mui-theme';
import {Grid, LinearProgress, Typography} from '@mui/material';
import concat from 'lodash/concat';
import useInterval from '@use-it/interval';

export const SLIDE_DELAY_IN_SC = 3;
const DESCRIPTION_MAX_LENGTH = 116;

export default ({
  T,
  appStyles,
  isMenuItem,
  useCategoryImage,
  isGiftItem,
  isDeal,
  isNew,
  title,
  description,
  onClick,
  refEl,
  imagePreview,
  imageKey,
  itemToAnimate,
  image,
  additionalImages,
  isImageByUrl,
  isLocked,
  userUnlockedDeal,
  shouldAnimateCard,
  lockDescription,
  classNames,
  style,
  loading,
  imageObjectFit,
  allergens,
  prices,
  isFlexColumn = false,
  currencySymbol,
  expirationDate,
  isDealUsedInOrder,
  actionsRenderer,
  totalDealPrice,
  giftPrice,
  menuItemId,
  businessAppConfiguration,
  userLoggedIn,
  calories
}) => {
  const {
    orderItemsCardHeight,
    dealItemsCardHeight,
    imageItemFlexLayout,
    dealItemFlexLayout,
    ListCardTitle,
    displayDealBadge,
    hideCurrencyInDealBadge,
    separateCentsPriceInDealBadge = true,
    DealBadge = {},
    GiftBadge = {},
    MenuItemPrice,
  } = appStyles;

  const enableFavoriteMenuItems = _.get(businessAppConfiguration, "enableFavoriteMenuItems") && userLoggedIn;

  const itemFlexLayout =
    isDeal || isGiftItem ? dealItemFlexLayout || "column" : imageItemFlexLayout;

  const isFlexRow = itemFlexLayout === "row";
  const customObjectFit = _.get(imageObjectFit, "objectFit");

  const orderItemImageFitStyle = customObjectFit
    ? customObjectFit === "fit" || customObjectFit === "contain"
      ? { objectFit: "contain" }
      : { objectFit: "cover" }
    : appStyles.orderItemImageFit && { objectFit: appStyles.orderItemImageFit };

  const itemHeight =
    isDeal || isGiftItem ? dealItemsCardHeight || 400 : orderItemsCardHeight;
  const imageHeight =
    orderItemsCardHeight &&
    _.toNumber(_.replace(_.replace(itemHeight, "px", ""), "pt", "")) / 2;

  const translatedDescription = description ? T(description) : "";

  const withPoints = isGiftItem && _.isNumber(giftPrice);

  const defaultItemImage = getAppMedia(mediaTypes.defaultItemImage, appStyles);

  const { classes } = useStyles();
  const sliderRef = useRef();
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [transitionDuration, setTransitionDuration] = useState(SLIDE_DELAY_IN_SC); // Initial transition duration
  const [delay, setDelay] = useState(SLIDE_DELAY_IN_SC * 1000); // Initial transition duration

  const restartLinesProgresses = () => {
    setTransitionDuration(0) // for the css animation to 0 immediately relax
    setProgressPercentage(0);

    setTimeout(() => {
      // and only then ignite the animation to 100%
      setTransitionDuration(SLIDE_DELAY_IN_SC) // for the css animation to start slow progress from 0 immediately again
      setProgressPercentage(100);
    })
  }

  useInterval(() => {
    if (!sliderRef.current) return;

    const itemsLength = concat([{imageKey}], additionalImages).length;
    const nextActiveSlideIndex = (activeSlideIndex + 1) % itemsLength;

    if (nextActiveSlideIndex === 0) {
      // back to start situation
      restartLinesProgresses();
    }
    else {
      // normal situation
      setTransitionDuration(SLIDE_DELAY_IN_SC)
      setProgressPercentage(100);
    }

    sliderRef.current.slickGoTo(nextActiveSlideIndex);
    setActiveSlideIndex(nextActiveSlideIndex);
  }, delay);

  const settings = {
    lazyLoad: "ondemand",
    arrows: false,
    infinite: true,
    fade: 1,
    onSwipe: side => {
      const delta = side === 'left' ? -1 : 1;
      const itemsLength = concat([{imageKey}], additionalImages).length;
      const nextActiveSlideIndex = (activeSlideIndex + delta) % itemsLength;
      setDelay(delay + .1);
      restartLinesProgresses();
      setActiveSlideIndex(nextActiveSlideIndex);
    }
  };

  const imageEl = (imageKey, mediaType, imagePreview) => {
    const props = {
      mediaKey: imageKey,
      mediaType,
      sizes: menuItemSizes(isFlexRow),
      className: classnames(
        isFlexRow ? styles.RowFlexLayout : styles.ColumnFlexLayout,
        isLocked && styles.LockedDealImage,
        userUnlockedDeal && styles.UnlockedDealImage,
      ),
      imgStyle: {
        ...orderItemImageFitStyle,
        ...(withPoints && {objectFit: 'cover'}),
        ...appStyles.imageObjectPosition && {objectPosition: appStyles.imageObjectPosition},
      },
      wrapperStyle: {height: isFlexRow ? itemHeight : imageHeight},
    }
    if (imagePreview) props.imagePreview = imagePreview;

    return <Image {...props}/>
  }

  const defaultItemEl = (mediaKey) => <Image
    mediaKey={mediaKey}
    mediaType={mediaTypes.defaultItemImage}
    sizes="400px"
    className={
      isFlexRow ? styles.RowFlexLayout : styles.ColumnFlexLayout
    }
    imgStyle={{
      objectFit: "contain",
      top: "25%",
      left: "25%",
      width: "50%",
      height: "50%",
      backgroundColor:
        appStyles.defaultImageBackgroundColor || "#eee",
    }}
    wrapperStyle={{
      backgroundColor:
        appStyles.defaultImageBackgroundColor || "#eee",
      ...(isFlexRow
        ? { height: itemHeight }
        : { height: imageHeight }),
    }}
  />

  const slider = (showMainImage) => (
    <div 
        style={{height: isFlexRow ? itemHeight : imageHeight, width: "100%"}}    
        className={classnames(classes.root,
          isFlexRow ? styles.RowFlexLayout : styles.ColumnFlexLayout,
          isLocked && styles.LockedDealImage,
          userUnlockedDeal && styles.UnlockedDealImage,
        )}    
        >
    <Grid
      container
      direction="column"
      style={{
        position: 'relative',
        width: "100%"
      }}
    >
      <Grid
        item
        style={{
          width: "100%"
        }}
      >
        <Slider
          ref={sliderRef}
          {...settings}
          className={classes.slider}
          style={{width: "100%", height: "100%", justifyContent: "center", contain: "inline-size"}}
        >

          {/* the main default image: */}
          {showMainImage && imageEl(
            imageKey,
            isDeal
              ? mediaTypes.deal
              : withPoints
                ? mediaTypes.coupon
                : useCategoryImage
                  ? mediaTypes.menuCategory
                  : mediaTypes.menuItem
            , imagePreview)}

          {/* the additional images: */}
          {map(additionalImages, (mediaItem, index) => imageEl(
            mediaItem.imageKey,
            mediaTypes.additionalImageMenuItem
          ))}
        </Slider>
      </Grid>
      <Grid
        container
        direction="row"
        spacing={1}
        justifyContent="space-evenly"
        sx={{
          marginTop: {
            xs: '-27px',
            sm: '-8px',
          },
        }}
      >
        {map(concat([{imageKey}], additionalImages), (mediaItem, index) => (
          <Grid
            key={mediaItem.key + `${index}`}
            item
            style={{
              flexGrow: 1,
              height: '23px',
              marginBottom: '-14px',
              paddingTop: '10px',
              zIndex: 3,
            }}
            onClick={e => {
              e.stopPropagation();

              setDelay(delay + .1); // restart the progress line interval time
              sliderRef.current.slickGoTo(index);

              setActiveSlideIndex(index);
              setTransitionDuration(0)
              setProgressPercentage(0);

              setTimeout(() => {
                setTransitionDuration(SLIDE_DELAY_IN_SC)
                setProgressPercentage(100);
              })
            }}
          >
            <LinearProgress
              className={
                (transitionDuration === 0 || index !== activeSlideIndex)
                  ? classes.customProgressBar0s
                  : classes[`customProgressBar${SLIDE_DELAY_IN_SC}s`]
              }
              value={
                index === activeSlideIndex
                  ? progressPercentage
                  : (index < activeSlideIndex ? 99.99 /* for css animation it's should be not 100*/ : 0)
              }
              variant="determinate"
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
    </div>
  )


  return (
    <List.Card
      classNames={classNames}
      appStyles={appStyles}
      onClick={onClick}
      refEl={refEl}
      animateCard={shouldAnimateCard}
      style={{
        position: 'relative',
        ...(isFlexRow
          ? {
            height: orderItemsCardHeight,
            flexDirection: 'row',
          }
          : {
            flexDirection: 'column',
            paddingBottom: '10px',
          }),
        ...style,
      }}
    >
      {loading && (
        <div className={styles.MenuItemLoader}>
          <AppContainer.CenteredColumn
            style={{height: "100%", justifyContent: "center" }}
          >
            <Loader appStyles={appStyles} />
          </AppContainer.CenteredColumn>
        </div>
      )}
      {isLocked && !userUnlockedDeal && (
        <div
          style={{ color: appStyles.lockIconColor }}
          className={classnames(styles.LockedDisplay, isFlexRow && styles.Row)}
        >
          {isGiftItem && (
            <span style={{ textAlign: "center", fontSize: "0.8rem" }}>
              {title}
            </span>
          )}
          <span
            className={classnames(
              styles.LockClosedIcon,
              userUnlockedDeal && styles.Unlocked,
              isFlexRow && styles.Row,
            )}
          >
            <LockClosedIcon />
          </span>
          {lockDescription && (
            <span
              style={{
                textAlign: "center",
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              {lockDescription}
            </span>
          )}
        </div>
      )}
      {itemToAnimate && (
        <Confetti
          height={itemToAnimate.innerHeight}
          width={itemToAnimate.innerWidth}
          colors={[appStyles.accentColor, appStyles.actionColor, "#fff"]}
          numberOfPieces={600}
          recycle={false}
          initialVelocityX={4}
          initialVelocityY={4}
        />
      )}
      {isLocked && (
        <div
          className={classnames(
            styles.LockedItemOverlay,
            userUnlockedDeal && styles.Unlocked,
          )}
        />
      )}
      {!isImageByUrl && (
        <div
          className={classnames(isFlexRow && styles.RowFlexLayout)}
          style={{
            position: "relative",
            color: appStyles.lockIconColor,
          }}
        >
          {(userUnlockedDeal || isDealUsedInOrder) && (
            <div>
              <div
                className={styles.LockOpenFlag}
                style={
                  isDealUsedInOrder || userUnlockedDeal
                    ? { borderTopColor: appStyles.accentColor }
                    : { borderTopColor: "rgba(0,0,0,0.7)" }
                }
              />
              {userUnlockedDeal ? (
                <LockOpenIcon className={classnames(styles.LockOpenIcon)} />
              ) : (
                <GiftIcon className={classnames(styles.GiftIcon)} />
              )}
            </div>
          )}

          {isDeal && displayDealBadge && (
            <div
              style={{
                position: "absolute",
                left: 0,
                margin: 0,
                top: 0,
                width: "calc(100% - 4px)",
                height: imageHeight,
                border: "2px dashed #777",
                zIndex: 2,
              }}
            />
          )}

          {isDeal && displayDealBadge && _.isNumber(totalDealPrice) && (
            <div
              style={{
                position: "absolute",
                right: "1em",
                top: 0,
                height: 56,
                width: 70,
                zIndex: 3,
                background: appStyles.accentColor,
                ...DealBadge,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  paddingTop: 10,
                }}
              >
                {!hideCurrencyInDealBadge && <div style={{ fontSize: 10 }}>$</div>}
                {separateCentsPriceInDealBadge ? (<>
                  <div style={{ fontSize: 36 }}>
                    {totalDealPrice.toString().split(".")[0]}
                  </div>
                  <div style={{ fontSize: 16 }}>
                    {_.get(totalDealPrice.toString().split("."), "1", "")}
                  </div>
                </>) : (
                  <div style={{ fontSize: 36 }}>
                    {totalDealPrice.toString()}
                  </div>
                ) }
                
              </div>
            </div>
          )}

          {withPoints && (
            <div
              style={{
                position: "absolute",
                right: "1em",
                top: 0,
                height: "auto",
                width: 70,
                zIndex: 3,
                background: appStyles.accentColor,
                ...GiftBadge,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: 10,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 30 }}>{giftPrice}</div>
                <div style={{ fontSize: 10 }}>{T("Points")}</div>
              </div>
            </div>
          )}

          {imageKey && !isImageByUrl ? (
            (appStyles.alternativeItemImagesShowIn === 'item' || !additionalImages?.length) ? (
                imageEl(
                  imageKey,
                  isDeal
                    ? mediaTypes.deal
                    : withPoints
                      ? mediaTypes.coupon
                      : useCategoryImage
                        ? mediaTypes.menuCategory
                        : mediaTypes.menuItem
                  , imagePreview)
              ) : (
                slider(true)
              )
          ) : defaultItemImage ? (
              (appStyles.alternativeItemImagesShowIn === 'item' || !additionalImages?.length) ? (
                defaultItemEl(defaultItemImage.imageKey)
                ) : (
                  slider(false)
                )
          ) : (
            <List.Card.Image
              classNames={classnames(
                isLocked && styles.LockedDealImage,
                userUnlockedDeal && styles.UnlockedDealImage,
                isFlexRow ? styles.RowFlexLayout : styles.ColumnFlexLayout,
              )}
              height={isFlexRow ? orderItemsCardHeight : 200}
            >
              <PlateIcon />
            </List.Card.Image>
          )}
          <AllergensDecorator
            allergens={allergens}
            appStyles={appStyles}
            wrapperClassNames={classnames(
              !isFlexRow && styles.HideOnMobile,
              styles.HideOnDesktop,
              styles.AllergensDecorator,
              styles.RowLayout,
              appStyles.rtl && styles.RTL,
            )}
          />
        </div>
      )}
      {isImageByUrl && (
        /*
        * ImageByUrl is old feature that is not used anymore.
        * when we did add a deal, for some time we use to add the images by url.
        * now we add the images by upload.
        * */
        <List.Card.Image height={200} width="100%">
          <Img
            height={200}
            width="100%"
            style={{
              maxWidth: "100%",
              maxHeight: "auto",
              objectFit: "cover",
              ...appStyles.imageObjectPosition && {objectPosition: appStyles.imageObjectPosition},
            }}
            src={image}
          />
        </List.Card.Image>
      )}
      <List.Card.Content tight={isFlexRow}>
        {isNew && (
          <div style={{ color: appStyles.actionColor }}>
            {T("NEW")}
          </div>
        )}
        <List.Card.Title appStyles={appStyles}>
          <span
            style={{
              justifyContent: "space-between",
              display: "flex",
              flexGrow: 1,
            }}
            className={classnames(isFlexRow && styles.MenuItemTitle)}
          >
            <span>
              <strong style={ListCardTitle}>
                {isMenuItem || isGiftItem || (isDeal && title)
                  ? title
                  : description}
              </strong>
            </span>
            {isMenuItem && !appStyles.hidePricesInOrderView && prices && (
              <span style={{ fontWeight: "bold", ...MenuItemPrice }}>
                <span style={{ paddingRight: currencySymbol !== " " ? 2 : 0 }}>
                  {displayPrices(prices, {
                    currencySymbol,
                    fixedPoint: appStyles.pricesFixedPoint,
                    shouldDisplayMinOnly: appStyles.displayOnlyMinPrice,
                  }, appStyles)}
                </span>
              </span>
            )}
          </span>
        </List.Card.Title>
        {(isGiftItem || (isDeal && title)) && (
          <List.Card.Description style={{ height: "auto" }}>
            <List.Card.Description.ExtraInfo>
              <div>
                <span>
                  {translatedDescription}
                  <br />
                  <br />
                  {expirationDate && (
                    <span>
                      <CalendarIcon />
                      {` ${T("Valid until:")} ` +
                        moment(new Date(expirationDate)).format(
                          appStyles.giftsExpirationDateFormat || "MMM Do YY",
                        )}
                    </span>
                  )}
                </span>
              </div>
            </List.Card.Description.ExtraInfo>
          </List.Card.Description>
        )}
        {isMenuItem && (
          <React.Fragment>
            <List.Card.Description>
              {translatedDescription.length > DESCRIPTION_MAX_LENGTH
                ? translatedDescription.slice(0, DESCRIPTION_MAX_LENGTH) + "..."
                : translatedDescription}
            </List.Card.Description>
            <Grid container justifyContent="space-between" mt="6px">
              {/* The space-between on the Grid container will push the first item to the left*/}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "85%",
                }}
              >
                <AllergensDecorator
                  allergens={allergens}
                  appStyles={appStyles}
                  wrapperClassNames={classnames(
                    styles.AllergensDecorator,
                    styles.Wrapper,
                    itemFlexLayout === "row" && styles.HideOnMobile
                  )}
                />
                {calories && !appStyles.hideCaloriesForMenuItems && (
                  <Typography component={"div"} sx={{ minWidth: "70px" }}>
                    {calories} Cal
                  </Typography>
                )}
              </Box>
              {/* The space-between on the Grid container will push the second item to the right*/}
              {enableFavoriteMenuItems && (
                <FavoriteButton
                  sx={{ zIndex: 3, padding: 0, margin: 0 }}
                  menuItemId={menuItemId}
                />
              )}
            </Grid>
          </React.Fragment>
        )}
        {_.isFunction(actionsRenderer) && actionsRenderer()}
      </List.Card.Content>
    </List.Card>
  );
};

const displayPrice = (price, { currencySymbol, fixedPoint }) =>
  `${currencySymbol}${fixedPoint ? price.toFixed(fixedPoint) : price}`;

const displayPrices = (
  prices,
  { currencySymbol, fixedPoint, shouldDisplayMinOnly },
  appStyles
) => {
  const priceRange = _.castArray(prices).slice(0, 2);
  if (appStyles.hidePlusSign && priceRange.length === 1 && priceRange[0] < 0){
    return `${currencySymbol}${-1 * priceRange[0]} off`;
  }

  if (_.some(priceRange, _.negate(_.isNumber))) {
    return "";
  }
  if (
    priceRange.length === 1 ||
    (priceRange.length === 2 && shouldDisplayMinOnly)
  ) {
    return displayPrice(priceRange[0], { currencySymbol, fixedPoint });
  }
  if (priceRange.length === 2) {
    return `${displayPrice(priceRange[0], {
      currencySymbol,
      fixedPoint,
    })}-${displayPrice(priceRange[1], {
      currencySymbol,
      fixedPoint,
    })} `;
  }
  return "";
};

const menuItemSizes = (isFlexRow) =>
  `(max-width: ${breakpoints.values.sm - 1}px) ${
    isFlexRow ? 300 : 600
  }px, 600px`;


const useStyles = makeStyles()((theme) => ({
  [`customProgressBar${SLIDE_DELAY_IN_SC}s`]: {
    '& .MuiLinearProgress-barColorPrimary.MuiLinearProgress-bar1Determinate': {
      // To optimize CPU usage and reduce the frequency of invoking 'useInterval',
      // we are adjusting the LinearProgress transition duration for smoother updates.
      transitionDuration: `${SLIDE_DELAY_IN_SC}s`,
    },
  },
  customProgressBar0s: {
    '& .MuiLinearProgress-barColorPrimary.MuiLinearProgress-bar1Determinate': {
      transitionDuration: '0s', // Immediate transition
    },
  }
}));